import {
  cataloguePriceFailure,
  deriveCataloguePrice,
} from '~/lib/catalogue-price'
import { invalidSaleReporter, logNonFatal } from '~/lib/diagnostics'
import type {
  ItemType,
  ShopBootstrap,
  ShopCollectionProduct,
} from './bootstrap'
import type { CatalogueCandidate } from './catalogue'
import { compareByDisplayOrder } from './collection-order'
import {
  type DisplayMetadata,
  type JSONValue,
  readDisplayMetadata,
} from './display-metadata'
import { deriveCandidateContent } from './land-skin-art'

// The main-product hero row (GLOSSARY.md "Main product collection"): the
// shop-main-product collection rendered as hero cards in display_order
// ascending. The banner comes from the product's display metadata; the title
// comes from the native string catalog by gid (`tree_type_{gid}_title` /
// `landskin_{gid}_title`) — the metadata's title_key / description_key are
// unused, and the card carries no description.

// The badge background when the metadata's countdown_tag_color is absent or
// invalid (GLOSSARY.md "Hero countdown").
export const HERO_COUNTDOWN_DEFAULT_COLOR = '#3F97AA'

export interface HeroDisplay {
  bannerUrl: string
  countdownColor: string
  // The banner placeholder (GLOSSARY.md) as a paintable data URL; absent when
  // the metadata carries none or an unusable one.
  placeholderSrc?: string
}

// Catalogue identity in the featured collections is (content type, gid) —
// GLOSSARY.md "Owned land skin".
export type MainProductCandidate = CatalogueCandidate & {
  display: HeroDisplay
  // Server-clock ms; undefined means no client-side end gate and no badge.
  purchaseEndAtMs?: number
}

export type MainProductSource = Pick<
  ShopBootstrap,
  'collections' | 'itemTypes' | 'unlockedLandSkins' | 'unlockedTrees'
>

type MainProductOmissionReason =
  | 'invalid_collection_entry'
  | 'invalid_content'
  | 'invalid_display_metadata'
  | 'invalid_price'
  | 'invalid_purchase_end_at'
  | 'invalid_purchase_type'
  | 'missing_banner_url'
  | 'missing_product'
  | 'missing_required_asset'

function reportOmission(
  reason: MainProductOmissionReason,
  productIds: number[],
  gid?: number,
): void {
  logNonFatal('shop_main_product_omitted', {
    reason,
    product_ids: productIds.join(','),
    ...(gid === undefined ? {} : { gid: String(gid) }),
  })
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) > 0
}

// #RGB, #RRGGBB, or #RRGGBBAA — anything else falls back to the design
// default (GLOSSARY.md "Display metadata": an invalid color alone never omits
// the product).
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i

// The base64 of a WebP file: `RIFF` at byte 0 and `WEBP` at byte 8 — across
// base64's 3-byte groups that is `UklGR`, six chars of file size, then
// `XRUJQ` — followed by the payload. The placeholder contract is WebP only
// (GLOSSARY.md "Banner placeholder"); a PNG or a data-URL prefix pasted into
// the field fails this and is ignored.
const WEBP_BASE64 = /^UklGR[A-Za-z0-9+/]{6}XRUJQ[A-Za-z0-9+/]*={0,2}$/

// Who a hero card's display diagnostics name: the Store product, plus the
// content gid for a main product (a starter pack has several contents and
// no single gid).
export interface HeroDisplayIds {
  gid?: number
  productId: number
}

function displayDiagnosticParams(ids: HeroDisplayIds): Record<string, string> {
  return {
    product_id: String(ids.productId),
    ...(ids.gid === undefined ? {} : { gid: String(ids.gid) }),
  }
}

// null = no usable placeholder; the card shows its plain fill instead. Only a
// present but unusable value is reported — absence is the normal case for a
// product whose metadata predates the field.
function parseBannerPlaceholder(
  raw: JSONValue | undefined,
  ids: HeroDisplayIds,
): string | null {
  if (raw === undefined || raw === null) return null
  if (typeof raw === 'string' && WEBP_BASE64.test(raw)) {
    return `data:image/webp;base64,${raw}`
  }
  logNonFatal(
    'shop_invalid_banner_placeholder_ignored',
    displayDiagnosticParams(ids),
  )
  return null
}

export type HeroDisplayFailure =
  | 'invalid_display_metadata'
  | 'missing_banner_url'

// The banner, banner placeholder, and countdown colour every hero card reads
// from display metadata — main products and the starter pack alike
// (GLOSSARY.md "Display metadata"). A failure means the card cannot render
// and its product is omitted; the caller reports it under its own event.
export function parseHeroDisplay(
  metadata: DisplayMetadata | null,
  ids: HeroDisplayIds,
): { display: HeroDisplay } | { failure: HeroDisplayFailure } {
  if (!metadata) return { failure: 'invalid_display_metadata' }

  const { banner_img_url, banner_placeholder_base64, countdown_tag_color } =
    metadata
  if (typeof banner_img_url !== 'string' || banner_img_url.trim() === '') {
    return { failure: 'missing_banner_url' }
  }

  let countdownColor = HERO_COUNTDOWN_DEFAULT_COLOR
  if (
    typeof countdown_tag_color === 'string' &&
    HEX_COLOR.test(countdown_tag_color)
  ) {
    countdownColor = countdown_tag_color
  } else if (
    countdown_tag_color !== undefined &&
    countdown_tag_color !== null
  ) {
    logNonFatal(
      'shop_invalid_countdown_color_ignored',
      displayDiagnosticParams(ids),
    )
  }

  const placeholderSrc = parseBannerPlaceholder(banner_placeholder_base64, ids)
  return {
    display: {
      bannerUrl: banner_img_url,
      countdownColor,
      ...(placeholderSrc ? { placeholderSrc } : {}),
    },
  }
}

// 'expired' = purchase_end_at already passed server now: the card is simply
// never created — the live-removal rule applied at derivation time, not an
// error. 'invalid' = malformed value, omitted with diagnostics: we could not
// honor the removal contract, and a malformed field on a curated product is
// a backend configuration error (GLOSSARY.md "Hydrated Shop product").
type PurchaseEnd =
  | { kind: 'none' }
  | { kind: 'ends'; endMs: number }
  | { kind: 'expired' }
  | { kind: 'invalid' }

function parsePurchaseEnd(
  raw: unknown,
  nowMs: number,
  productId: number,
  gid: number,
): PurchaseEnd {
  if (raw === undefined || raw === null) return { kind: 'none' }
  const endMs = typeof raw === 'string' ? Date.parse(raw) : Number.NaN
  if (!Number.isFinite(endMs)) {
    reportOmission('invalid_purchase_end_at', [productId], gid)
    return { kind: 'invalid' }
  }
  return endMs > nowMs ? { kind: 'ends', endMs } : { kind: 'expired' }
}

function deriveEntryCandidate(
  entry: ShopCollectionProduct,
  itemTypes: Map<number, ItemType>,
  ownedTreeGids: Set<number>,
  ownedLandSkinGids: Set<number>,
  nowMs: number,
): MainProductCandidate | null {
  const product = entry.item_type_product
  if (!product) {
    reportOmission('missing_product', [entry.item_type_product_id ?? entry.id])
    return null
  }

  const itemType = itemTypes.get(product.item_type_id ?? -1)
  const content = itemType?.item_contents?.[0]
  if (
    !itemType ||
    !content ||
    (content.type !== 'tree_type' && content.type !== 'land_skin') ||
    !isPositiveInteger(content.value)
  ) {
    reportOmission('invalid_content', [product.id])
    return null
  }
  const contentType = content.type
  const gid = content.value

  const priceFailure = cataloguePriceFailure(product)
  if (priceFailure) {
    reportOmission(priceFailure, [product.id], gid)
    return null
  }

  const metadata = readDisplayMetadata(product.display_metadata)
  const heroDisplay = parseHeroDisplay(metadata, { gid, productId: product.id })
  if ('failure' in heroDisplay) {
    reportOmission(heroDisplay.failure, [product.id], gid)
    return null
  }
  const { display } = heroDisplay

  // A land-skin hero's card paints only its banner, but its detail is one
  // tap away and renders the icon-or-detail image, so the icon stays
  // required display data here too (GLOSSARY.md "Land-skin art").
  const candidateContent = deriveCandidateContent(
    contentType,
    itemType,
    metadata,
    product.id,
    gid,
  )
  if (!candidateContent) {
    reportOmission('missing_required_asset', [product.id], gid)
    return null
  }

  const purchaseEnd = parsePurchaseEnd(
    product.purchase_end_at,
    nowMs,
    product.id,
    gid,
  )
  if (purchaseEnd.kind === 'invalid' || purchaseEnd.kind === 'expired') {
    return null
  }

  // Owned-or-purchaseable visibility, owned taking precedence (GLOSSARY.md
  // "Main product collection"). Tree and land-skin gids are separate
  // namespaces, each judged against its own ownership list.
  const owned =
    contentType === 'tree_type'
      ? ownedTreeGids.has(gid)
      : ownedLandSkinGids.has(gid)
  if (!owned && product.purchaseable !== true) return null

  return {
    ...candidateContent,
    display,
    gid,
    owned,
    price: deriveCataloguePrice(product, invalidSaleReporter(product.id, gid)),
    product,
    purchaseEndAtMs:
      purchaseEnd.kind === 'ends' ? purchaseEnd.endMs : undefined,
  }
}

// The hero row's pre-hydration facts in final display order; slots never
// reorder after creation (ADR 0024's stable-slot rule). nowMs is the Shop
// server clock at creation: an already-passed purchase_end_at never creates
// a slot, and later expiry is the view's live judgment.
export function deriveMainProductCandidates(
  source: MainProductSource,
  nowMs: number,
): MainProductCandidate[] {
  const itemTypes = new Map<number, ItemType>()
  for (const itemType of source.itemTypes) {
    if (!itemType || typeof itemType !== 'object') continue
    itemTypes.set(itemType.id, itemType)
  }
  const ownedTreeGids = new Set(
    source.unlockedTrees.map((tree) => tree.gid).filter(isPositiveInteger),
  )
  const ownedLandSkinGids = new Set(
    source.unlockedLandSkins.map((skin) => skin.gid).filter(isPositiveInteger),
  )

  return source.collections[
    'shop-main-product'
  ].item_type_product_collection_products
    .filter((entry) => {
      if (entry && typeof entry === 'object') return true
      reportOmission('invalid_collection_entry', [])
      return false
    })
    .sort(compareByDisplayOrder)
    .flatMap((entry) => {
      const candidate = deriveEntryCandidate(
        entry,
        itemTypes,
        ownedTreeGids,
        ownedLandSkinGids,
        nowMs,
      )
      return candidate ? [candidate] : []
    })
}
